import type * as React from 'react'
import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import {
  AlertDialog,
  Button,
  DropdownMenu,
  toast,
  TrashIcon,
} from '@buffer-mono/popcorn'

import { useDeleteContentItem } from './useDeleteContentItem'
import type { ContentItemDraft } from './usePaginatedContentItems'

type DeleteContentItemMenuItemProps = {
  contentItemId: string
  allowedActions: ContentItemDraft['allowedActions']
}

export function DeleteContentItemMenuItem({
  contentItemId,
  allowedActions,
}: DeleteContentItemMenuItemProps): JSX.Element | null {
  const [deleteContentItem, { loading }] = useDeleteContentItem()
  const [dialogOpen, setDialogOpen] = useState(false)

  const handleConfirmDelete: React.MouseEventHandler = useCallback(
    async (e) => {
      e.preventDefault()

      const { success, error } = await deleteContentItem({
        variables: { id: contentItemId },
      })

      if (!success) {
        toast.error(`Failed to delete draft: ${error.message}`)
        return
      }

      setDialogOpen(false)
      toast.success('Draft was deleted')
    },
    [deleteContentItem, contentItemId],
  )

  if (!allowedActions.includes('deleteContentItem')) {
    return null
  }

  return (
    <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <AlertDialog.Trigger>
        <DropdownMenu.Item
          variant="critical"
          // preventDefault keeps the dropdown item from closing the menu so the
          // AlertDialog can open on top of it.
          onSelect={(e): void => {
            e.preventDefault()
          }}
        >
          <TrashIcon />
          Delete
        </DropdownMenu.Item>
      </AlertDialog.Trigger>
      <AlertDialog.Content>
        <AlertDialog.Title>Delete draft?</AlertDialog.Title>
        <AlertDialog.Description>
          This draft will be permanently deleted. This action cannot be undone.
        </AlertDialog.Description>
        <AlertDialog.Actions>
          <AlertDialog.Cancel>
            <Button variant="tertiary">Cancel</Button>
          </AlertDialog.Cancel>
          <AlertDialog.Action>
            <Button
              variant="critical"
              loading={loading}
              onClick={handleConfirmDelete}
            >
              {loading ? 'Deleting...' : 'Delete'}
            </Button>
          </AlertDialog.Action>
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}
